import React, { useMemo } from 'react';
import './MarcJuniorPopup.css';
import SeeFilesButton from './SeeFilesButton';
import { getDriveFolderUrl } from './driveFolders';

function fmtEur(value, showSign = false) {
  if (value == null || isNaN(value)) return '—';
  const sign = showSign ? (value >= 0 ? '+' : '−') : '';
  return `${sign}€${Math.round(Math.abs(value)).toLocaleString()}`;
}

function formatDateLong(dateStr) {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  if (isNaN(d)) return dateStr;
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

// Light popup for the "Marc Junior NV" card — Kristof's 25% interest in a
// family NV holding two adjacent properties. Driven by investment.marcJuniorDetail.
function MarcJuniorPopup({ onClose, investment }) {
  const d = investment?.marcJuniorDetail || {};
  const own = d.ownership_pct != null ? d.ownership_pct / 100 : 0.25;

  const x = useMemo(() => {
    const propLow = d.property_low_eur ?? null;
    const propHigh = d.property_high_eur ?? null;
    const cash = d.cash_eur ?? null;
    const debt = d.debt_eur ?? null;
    const taxLow = d.exit_tax_low_eur ?? null;
    const taxHigh = d.exit_tax_high_eur ?? null;
    // 100% pre-tax equity range (share-deal): property + cash − debt
    const eqLow = (propLow != null && cash != null && debt != null) ? propLow + cash - debt : null;
    const eqHigh = (propHigh != null && cash != null && debt != null) ? propHigh + cash - debt : null;
    // Kristof's pre-tax share
    const myLow = eqLow != null ? eqLow * own : null;
    const myHigh = eqHigh != null ? eqHigh * own : null;
    // Kristof's after-tax share
    const myAfterLow = (myLow != null && taxLow != null) ? myLow - taxLow : null;
    const myAfterHigh = (myHigh != null && taxHigh != null) ? myHigh - taxHigh : null;
    return { propLow, propHigh, cash, debt, eqLow, eqHigh, myLow, myHigh, myAfterLow, myAfterHigh };
  }, [d, own]);

  return (
    <div className="popup-overlay" onClick={onClose}>
      <div className="popup-content mj-popup-content" onClick={(e) => e.stopPropagation()}>
        <div className="mj-header">
          <h2>Marc Junior NV — {Math.round(own * 100)}% interest</h2>
          <div className="popup-header-actions">
            <SeeFilesButton url={getDriveFolderUrl('Marc Junior NV')} />
            <button className="mj-close-btn" onClick={onClose}>✕</button>
          </div>
        </div>

        {/* KPI row */}
        <div className="mj-kpis">
          <div className="mj-kpi">
            <span className="mj-label">My share (pre-tax)</span>
            <span className="mj-big">{fmtEur(investment?.eurValue)}</span>
            <span className="mj-sub">
              {x.myLow != null && x.myHigh != null ? `range ${fmtEur(x.myLow)}–${fmtEur(x.myHigh)}` : ''}
            </span>
          </div>
          <div className="mj-kpi">
            <span className="mj-label">My share (after exit tax)</span>
            <span className="mj-mid">
              {x.myAfterLow != null && x.myAfterHigh != null
                ? `${fmtEur(x.myAfterLow)}–${fmtEur(x.myAfterHigh)}`
                : (d.after_tax_eur != null ? fmtEur(d.after_tax_eur) : '—')}
            </span>
            <span className="mj-sub">Belgian corporate exit tax on the property gain</span>
          </div>
          <div className="mj-kpi">
            <span className="mj-label">Book equity (my share)</span>
            <span className="mj-mid">{d.book_equity_eur != null ? fmtEur(d.book_equity_eur * own) : '—'}</span>
            <span className="mj-sub">{d.book_equity_eur != null ? `of €${Math.round(d.book_equity_eur).toLocaleString()} total` : ''}</span>
          </div>
        </div>

        <div className="mj-note mj-note--warn">
          <span className="mj-note-tag">Pre-tax / pre-sale</span>
          Headline is an estimate of {Math.round(own * 100)}% of the pre-tax share-deal equity. Not yet realised — the
          sale is at an early stage and the figure moves with the final price and exit tax.
        </div>

        {/* Asset / liability bridge */}
        <div className="mj-section-title">Bridge to equity (100% of the NV)</div>
        <div className="mj-facts">
          <div className="mj-row">
            <span>Property — Vrijheidsstraat 37 + 39</span>
            <strong>{x.propLow != null ? `${fmtEur(x.propLow)}–${fmtEur(x.propHigh)}` : '—'}</strong>
          </div>
          <div className="mj-row"><span>+ Cash</span><strong>{fmtEur(x.cash)}</strong></div>
          <div className="mj-row"><span>− Debt {d.debt_note ? `(${d.debt_note})` : ''}</span><strong>−{fmtEur(x.debt)}</strong></div>
          <div className="mj-row mj-row--total">
            <span>= Pre-tax equity (share-deal)</span>
            <strong>{x.eqLow != null ? `${fmtEur(x.eqLow)}–${fmtEur(x.eqHigh)}` : '—'}</strong>
          </div>
        </div>

        {/* Sale status */}
        {(d.sale_bid_eur != null || d.sale_ask_eur != null || d.sale_status) && (
          <>
            <div className="mj-section-title">Live sale</div>
            <div className="mj-facts">
              {d.sale_bid_eur != null && (
                <div className="mj-row"><span>Developer bid (DS Architecten, VAT incl.)</span><strong>{fmtEur(d.sale_bid_eur)}</strong></div>
              )}
              {d.sale_ask_eur != null && (
                <div className="mj-row"><span>Asking price</span><strong>{fmtEur(d.sale_ask_eur)}</strong></div>
              )}
              {d.sale_condition && (
                <div className="mj-row mj-row--wrap"><span>Condition</span><strong>{d.sale_condition}</strong></div>
              )}
              {d.sale_status && (
                <div className="mj-row mj-row--wrap"><span>Status</span><strong>{d.sale_status}</strong></div>
              )}
              {d.agent && (
                <div className="mj-row"><span>Agent</span><strong>{d.agent}</strong></div>
              )}
            </div>
          </>
        )}

        <div className="mj-facts mj-facts--meta">
          {d.ownership_note && (
            <div className="mj-row mj-row--wrap"><span>Ownership</span><strong>{d.ownership_note}</strong></div>
          )}
          {d.balans_date && (
            <div className="mj-row"><span>Balans date</span><strong>{formatDateLong(d.balans_date)}</strong></div>
          )}
        </div>

        <div className="mj-foot">
          {d.source ? `Source: ${d.source}.` : ''}
          {d.as_of ? ` Last updated ${formatDateLong(d.as_of)}.` : ''}
        </div>
      </div>
    </div>
  );
}

export default MarcJuniorPopup;
